import FileSaver from 'file-saver';
import qs from 'qs';
import { Page } from '../model/elements';
import { EntityWithStatus, ProjectMember, ProjectMemberImport, TaskMember } from '../model/entities';
import { proxyApiAxios } from './CustomAxios';
import providerAuthService from '../services/ProviderAuthService';

class ProjectMemberApi {
    list = async (
        page: number,
        size: number,
        sortField: string,
        sortOrder: boolean,
        grouped: boolean,
        hubId: string,
        projectId: string[],
        searchText?: string,
        platform?: 'ACC' | 'BIM360',
    ): Promise<Page<ProjectMember>> => {
        const headers = await providerAuthService.getRequestHeader('ACC');
        const response = await proxyApiAxios.get<Page<ProjectMember>>('/project-members', {
            params: {
                page,
                size,
                sortField,
                sortOrder,
                grouped,
                hubId,
                projectId,
                searchText,
                platform,
            },
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers,
        });
        response.data.sort.field = sortField;
        response.data.sort.order = sortOrder;

        return response.data;
    };

    updateCompany = async (
        projectMembers: ProjectMember[],
        hubId: string,
    ): Promise<EntityWithStatus<ProjectMember>[]> => {
        const headers = await providerAuthService.getRequestHeader('ACC');
        const response = await proxyApiAxios.patch<EntityWithStatus<ProjectMember>[]>(
            `/project-members`,
            projectMembers,
            {
                params: { hubId, company: '' },
                headers,
            },
        );
        return response.data;
    };

    updateRoles = async (
        projectMembers: ProjectMember[],
        hubId: string,
    ): Promise<EntityWithStatus<ProjectMember>[]> => {
        const headers = await providerAuthService.getRequestHeader('ACC');
        const response = await proxyApiAxios.patch<EntityWithStatus<ProjectMember>[]>(
            `/project-members`,
            projectMembers,
            {
                params: { hubId, roles: '' },
                headers,
            },
        );
        return response.data;
    };

    exportSpreadSheet = async (
        sortField: string,
        sortOrder: boolean,
        hubId: string,
        projectId: string[],
        grouped: boolean,
        searchText?: string,
        platform?: 'ACC' | 'BIM360',
    ) => {
        const headers = await providerAuthService.getRequestHeader('ACC');
        const response = await proxyApiAxios.get<Blob>(`/project-members/spreadsheets`, {
            responseType: 'blob',
            params: { sortField, sortOrder, hubId, projectId, grouped, searchText, platform },
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers,
        });
        FileSaver.saveAs(response.data, 'link-project-members.xlsx');
    };

    import = async (projectMemberImport: ProjectMemberImport): Promise<TaskMember> => {
        const headers = await providerAuthService.getRequestHeader('ACC');
        const response = await proxyApiAxios.post<TaskMember>('/project-members/imports', projectMemberImport, {
            headers,
        });

        return response.data;
    };

    loadSpreadsheet = async (file: any, hubId: string): Promise<ProjectMemberImport> => {
        const headers = await providerAuthService.getRequestHeader('ACC');
        const formData = this.buildFormData(hubId, file);
        const response = await proxyApiAxios.post<any>('/project-members/spreadsheets', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                ...headers,
            },
        });
        return response.data;
    };

    /**
     * Builds a form data for project members import.
     * @param hubId the hub id
     * @param file the file
     * @returns the formdata
     */
    private buildFormData = (hubId: string, file?: File) => {
        const formData: FormData = new FormData();
        formData.append('hubId', hubId);
        if (file) {
            formData.append('attachment', file);
        }

        return formData;
    };
}

const projectMemberApi: ProjectMemberApi = new ProjectMemberApi();
export default projectMemberApi;
